import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
// Replace react-toast-notifications with react-toastify
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
// import SplashScreen from './components/SplashScreen'; // Removed SplashScreen
import ServiceWorkerUpdater from './components/ServiceWorkerUpdater';
import MobileNavBar from './components/MobileNavBar';

// استيراد المكونات الأساسية
import NetworkStatus from './components/NetworkStatus';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';

import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

// Utilities
import { setupFontLoading } from './utils/fontLoader';
import { setupLazyLoading } from './utils/imageOptimization';
import { initializeOfflineSupport } from './utils/offlineSupport';

import { 
  registerServiceWorker, 
  setupServiceWorkerMessages,
  setupOnlineOfflineListeners
} from './utils/serviceWorkerHandler';

// Lazy load all screens with improved chunking for better performance
// Public screens (accessible without login)
const HomeScreen = React.lazy(() => import(/* webpackChunkName: "public-screens" */ './screens/HomeScreenWeb'));
const LoginScreen = React.lazy(() => import(/* webpackChunkName: "auth-screens" */ './screens/LoginScreenWeb'));
const RegisterScreen = React.lazy(() => import(/* webpackChunkName: "auth-screens" */ './screens/RegisterScreenWeb'));
const ClinicListScreen = React.lazy(() => import(/* webpackChunkName: "public-screens" */ './screens/ClinicListScreenWeb'));
const JobsScreenWeb = React.lazy(() => import(/* webpackChunkName: "public-screens" */ './screens/JobsScreenWeb'));
const ServicesScreen = React.lazy(() => import(/* webpackChunkName: "public-screens" */ './screens/ServicesScreenWeb'));

// Patient screens
const AppointmentScreen = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/AppointmentScreenWeb'));
const MobileAppointmentScreenWeb = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/MobileAppointmentScreenWeb'));
const NotificationsScreen = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/NotificationsScreenWeb'));
const PatientDocumentsScreenWeb = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/PatientDocumentsScreenWeb'));
const MobileDocumentsScreenWeb = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/MobileDocumentsScreenWeb'));
const PatientClinicChatScreenWeb = React.lazy(() => import(/* webpackChunkName: "chat-screens" */ './screens/PatientClinicChatScreenWeb'));
const HealthReportsScreenWeb = React.lazy(() => import(/* webpackChunkName: "patient-screens" */ './screens/HealthReportsScreenWeb'));

// Provider/Clinic screens
const ProviderDashboardScreen = React.lazy(() => import(/* webpackChunkName: "provider-screens" */ './screens/ProviderDashboardScreenWeb'));
const ProviderProfileScreen = React.lazy(() => import(/* webpackChunkName: "provider-screens" */ './screens/ProviderProfileScreenWeb'));
const ClinicClinicChatScreenWeb = React.lazy(() => import(/* webpackChunkName: "chat-screens" */ './screens/ClinicClinicChatScreenWeb'));

// Admin/Management screens
const AppointmentManagementScreen = React.lazy(() => import(/* webpackChunkName: "admin-screens" */ './screens/AppointmentManagementScreen'));
const DoctorManagementScreen = React.lazy(() => import(/* webpackChunkName: "admin-screens" */ './screens/DoctorManagementScreen'));
const PatientManagementScreen = React.lazy(() => import(/* webpackChunkName: "admin-screens" */ './screens/PatientManagementScreen'));
const ReportsAndStatisticsScreen = React.lazy(() => import(/* webpackChunkName: "admin-screens" */ './screens/ReportsAndStatisticsScreen'));

// Dashboard screens
const ClinicDashboard = React.lazy(() => import(/* webpackChunkName: "dashboard-screens" */ './dashboard/clinic/ClinicDashboard'));
const UserDashboard = React.lazy(() => import(/* webpackChunkName: "dashboard-screens" */ './dashboard/user/UserDashboard'));

function App() {
  // Initialize performance optimizations and utilities
  useEffect(() => {
    // Set up font loading optimization
    setupFontLoading({
      criticalFonts: [
        '/fonts/cairo-v20-arabic-regular.woff2',
        '/fonts/cairo-v20-arabic-700.woff2',
        '/fonts/roboto-v30-latin-regular.woff2'
      ],
      fontDisplay: 'swap',
      fonts: [
        { fontFamily: 'Cairo', options: { weight: '400' } },
        { fontFamily: 'Cairo', options: { weight: '700' } },
        { fontFamily: 'Roboto', options: { weight: '400' } }
      ]
    });
    
    // Set up image lazy loading
    setupLazyLoading('img[data-src]', 200);
    
    // Initialize offline support
    const { removeListeners, stopAutoSync, restoreFetch } = initializeOfflineSupport({
      offlinePageUrl: '/offline.html',
      criticalApiEndpoints: [
        { url: `${process.env.REACT_APP_API_URL}/services.php?action=list`, key: 'services' },
        { url: `${process.env.REACT_APP_API_URL}/clinics.php?action=list`, key: 'clinics' }
      ],
      offlineFallbackData: {
        'services': { data: [], isOfflineData: true },
        'clinics': { data: [], isOfflineData: true },
        'user_profile': { isOfflineData: true },
        'basic_settings': { isOfflineData: true }
      }
    });

    // Register service worker
    registerServiceWorker().then(registration => {
      console.log('Service Worker registered:', registration);
    });

    // Set up service worker message handler
    const removeMessageListener = setupServiceWorkerMessages((message) => {
      // Handle service worker messages
      if (message.type === 'ONLINE_MODE') {
        // Use a more direct approach to get language
        const language = document.documentElement.lang === 'ar' ? 'ar' : 'en';
        toast.success(language === 'ar' ? message.message : message.english_message || 'Internet connection restored');
      } else if (message.type === 'OFFLINE_MODE') {
        // Use a more direct approach to get language
        const language = document.documentElement.lang === 'ar' ? 'ar' : 'en';
        toast.warning(language === 'ar' ? message.message : message.english_message || 'You are now offline');
      } else if (message.type === 'SW_UPDATED') {
        // Use a more direct approach to get language
        const language = document.documentElement.lang === 'ar' ? 'ar' : 'en';
        toast.info(language === 'ar' ? message.message : message.english_message || 'App updated. Please refresh to apply changes.');
      }
    });

    // Cleanup function
    return () => {
      removeListeners();
      stopAutoSync();
      restoreFetch();
      removeMessageListener();
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <AppContent />
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { theme } = useTheme();
  const { language } = useLanguage();

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      <div className={`app-container ${theme}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <ServiceWorkerUpdater />
        <NetworkStatus />
        <main className="main-content">
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomeScreen />} />
                <Route path="/login" element={<LoginScreen />} />
                <Route path="/register" element={<RegisterScreen />} />
                <Route path="/clinics" element={<ClinicListScreen />} />

                {/* المسارات المحمية */}
                <Route path="/appointments" element={<ProtectedRoute><AppointmentScreen /></ProtectedRoute>} />
                <Route path="/mobile-appointments" element={<ProtectedRoute><MobileAppointmentScreenWeb /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsScreen /></ProtectedRoute>} />
                <Route path="/documents" element={<ProtectedRoute><PatientDocumentsScreenWeb /></ProtectedRoute>} />
                <Route path="/mobile-documents" element={<ProtectedRoute><MobileDocumentsScreenWeb /></ProtectedRoute>} />
                <Route path="/provider/dashboard" element={<ProtectedRoute><ProviderDashboardScreen /></ProtectedRoute>} />
                <Route path="/provider/profile" element={<ProtectedRoute><ProviderProfileScreen /></ProtectedRoute>} />
                <Route path="/chat/patient-clinic" element={<ProtectedRoute><PatientClinicChatScreenWeb /></ProtectedRoute>} />
                <Route path="/chat/clinic-clinic" element={<ProtectedRoute><ClinicClinicChatScreenWeb /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><HealthReportsScreenWeb /></ProtectedRoute>} />

                {/* مسار الملف الشخصي */}
                <Route path="/profile" element={<ProtectedRoute><ProviderProfileScreen /></ProtectedRoute>} /> 
                <Route path="/jobs" element={<JobsScreenWeb />} />
                <Route path="/services" element={<ServicesScreen />} />

                {/* Clinic Dashboard Routes */}
                <Route path="/dashboard/clinic" element={<ProtectedRoute><ClinicDashboard /></ProtectedRoute>} />
                <Route path="/dashboard/clinic/appointments" element={<ProtectedRoute><AppointmentManagementScreen /></ProtectedRoute>} />
                <Route path="/dashboard/clinic/doctors" element={<ProtectedRoute><DoctorManagementScreen /></ProtectedRoute>} />
                <Route path="/dashboard/clinic/patients" element={<ProtectedRoute><PatientManagementScreen /></ProtectedRoute>} />
                <Route path="/dashboard/clinic/reports" element={<ProtectedRoute><ReportsAndStatisticsScreen /></ProtectedRoute>} />

                {/* User Dashboard Routes */}
                <Route path="/dashboard/user" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
        <MobileNavBar />
      </div>
    </>
  );
}

export default App;